import * as React from 'react';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

//use component as so:
// <SelectAllMulti
// label="multi"
// options={options}
// setChecked={setChecked}                                        
// />
// where setChecked is the state change for the 'checked' selections

export default function SelectAllMulti(props: any) {
  const {label, disabled = false, options, setChecked, editable, addNewClicked, editClicked, deleteClicked, initialValues = [], setControlledValue = null, controlledValue = null } = props;
  
  // Helper function to get display label from option (supports both label and name properties)
  const getDisplayLabel = (option: any): string => {
    return option.label ?? option.name ?? '';
  };

  // sample options
  // const options = [
  //   { label: 'The Shawshank Redemption', id: 1994 },
  //   { label: 'The Godfather', id: 1972 },
  //   { label: 'The Godfather: Part II', id: 1974 },
  //   { label: 'The Dark Knight', id: 2008 },
  //   { label: '12 Angry Men', id: 1957 },
  // ];

  const [value, setValue] = React.useState(initialValues);
  const isControlled = controlledValue != null;
  
  React.useEffect(() => {
    if (isControlled)
    {
      setChecked(controlledValue);
    }
    else
    {
      setChecked(value);
    }
  }, [controlledValue, value]);
  
  if (!options)
  {
    return;
  }

  return (
    <Autocomplete
      multiple
      disabled={disabled}
      value={isControlled ? controlledValue : value}
      filterOptions={(options, params) => { // <<<--- inject the Select All option
        const filter = createFilterOptions()
        const filtered = filter(options, params)
        if (editable)
				{
					return [{ label: 'Select All...', all: true }, ...filtered, {addNew: true, addNewClicked: () => {}}]
				}
				else
				{
        	return [{ label: 'Select All...', all: true }, ...filtered]
				}
      }}
      onChange={(event, newValue) => {
        if (newValue.find(option => option.addNew)) {
          addNewClicked();
          return;
        }
        if (newValue.find(option => option.all)) {
          if (isControlled)
          {
            return setControlledValue(value.length === options.length ? [] : options);
          }
          else
          {
            return setValue(value.length === options.length ? [] : options);
          }
        }
        if (isControlled)
        {
          setControlledValue(newValue);
        }
        else
        {
          setValue(newValue);
        }
      }}
      options={options}
      disableCloseOnSelect
      getOptionLabel={(option) => (option.addNew ? '' : getDisplayLabel(option)) }
      isOptionEqualToValue={(option, value) => option.id === value.id}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          return (
            <Chip
              key={key}
              label={getDisplayLabel(option)}
              {...tagProps}
              style={{ backgroundColor: option.color }}
              sx={{ maxWidth: '100%' }}
            />
          );
        })
      }
      renderOption={(props, option, { selected }) => (
        (option.addNew ?
					<li {...props} key={-1}>
	          <Button
	            onClick={addNewClicked}
							style={{ marginRight: 8 }}
	          >
	          Add New
						</Button>
	        </li>
				:
        <li {...props} key={option.id}>
          <Checkbox
            icon={icon}
            checkedIcon={checkedIcon}
            style={{ marginRight: 8 }}
            checked={option.all ? !!(value.length === options.length) : selected}
          />
          {!!option.color && (
            <Box
              component="span"
              sx={{
                width: 14,
                height: 14,
                borderRadius: '50%',
                backgroundColor: option.color,
                marginRight: 1,
                flexShrink: 0,
              }}
            />
          )}
          {getDisplayLabel(option)}
          {!option.all && editable && editClicked &&
          <Button
            onClick={(e) => { e.stopPropagation(); editClicked(option.id); }}
            style={{ marginLeft: 8, marginRight: 4 }}
          >
          Edit
          </Button>
          }
          {!option.all && editable &&
          <Button
            onClick={(e) => { e.stopPropagation(); deleteClicked(option.id); }}
            style={{ marginRight: 8 }}
          >
          X
          </Button>
          }
        </li>
        )
      )}
      renderInput={(params) => (
        <TextField {...params} label={label}/>
      )}
      limitTags={3}
    />
  );
}


